import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Phone, Plus, Search, Clock, PhoneIncoming, PhoneOutgoing,
  Trash2, RefreshCw, User, Tag, CheckCircle2, XCircle, Timer
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const DIRECTIONS = { inbound: { label: 'Inbound', icon: PhoneIncoming, color: 'bg-blue-100 text-blue-700' }, outbound: { label: 'Outbound', icon: PhoneOutgoing, color: 'bg-purple-100 text-purple-700' } };
const OUTCOMES = { completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' }, missed: { label: 'Missed', color: 'bg-red-100 text-red-700' }, voicemail: { label: 'Voicemail', color: 'bg-amber-100 text-amber-700' }, busy: { label: 'Busy', color: 'bg-gray-100 text-gray-600' }, transferred: { label: 'Transferred', color: 'bg-blue-100 text-blue-700' } };

const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function CallRecordingPage() {
  const [recordings, setRecordings] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDir, setFilterDir] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(null);
  const [form, setForm] = useState({ caller_name: '', caller_phone: '', caller_email: '', direction: 'inbound', duration_seconds: 0, outcome: 'completed', agent_name: '', notes: '', tags: '' });

  const fetchData = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([
        axios.get(`${API}/api/call-recordings`),
        axios.get(`${API}/api/call-recordings/dashboard`),
      ]);
      setRecordings(r.data.recordings || []);
      setDashboard(d.data);
    } catch { toast.error('Failed to load recordings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/call-recordings`, {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        duration_seconds: parseInt(form.duration_seconds) || 0,
      });
      toast.success('Call log created!');
      setShowCreate(false);
      setForm({ caller_name: '', caller_phone: '', caller_email: '', direction: 'inbound', duration_seconds: 0, outcome: 'completed', agent_name: '', notes: '', tags: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteRecording = async (id) => {
    if (!window.confirm('Delete this recording?')) return;
    try { await axios.delete(`${API}/api/call-recordings/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const filtered = recordings.filter(r => {
    if (filterDir !== 'all' && r.direction !== filterDir) return false;
    if (search && !r.caller_name?.toLowerCase().includes(search.toLowerCase()) && !r.call_id?.toLowerCase().includes(search.toLowerCase()) && !r.caller_phone?.includes(search)) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="call-recording-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Call Recordings<PageInfoTooltip pageKey="call-recording" /></h1>
          <p className="text-gray-medium text-sm mt-1">Log and track all customer calls</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="log-call-btn"><Plus className="h-4 w-4 mr-1" />Log Call</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Phone} label="Total Calls" value={dashboard.total || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={PhoneIncoming} label="Inbound" value={dashboard.inbound || 0} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={PhoneOutgoing} label="Outbound" value={dashboard.outbound || 0} color="bg-purple-50 text-purple-600" />
        <StatCard icon={Timer} label="Avg Duration" value={formatDuration(dashboard.avg_duration || 0)} color="bg-amber-50 text-amber-600" />
      </div>

      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search calls..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="call-search" />
            </div>
            <Select value={filterDir} onValueChange={setFilterDir}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-medium"><Phone className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No call recordings yet</p></div>
          ) : (
            <div className="space-y-2">
              {filtered.map(rec => {
                const dir = DIRECTIONS[rec.direction] || DIRECTIONS.inbound;
                const outc = OUTCOMES[rec.outcome] || OUTCOMES.completed;
                return (
                  <div key={rec.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setShowView(rec)} data-testid={`call-row-${rec.id}`}>
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", dir.color)}>
                      <dir.icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-text-primary">{rec.call_id}</span>
                        <Badge className={cn("text-[10px]", dir.color)}>{dir.label}</Badge>
                        <Badge className={cn("text-[10px]", outc.color)}>{outc.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-medium truncate">{rec.caller_name || 'Unknown'} {rec.caller_phone ? `· ${rec.caller_phone}` : ''}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-text-primary">{formatDuration(rec.duration_seconds)}</p>
                      <p className="text-[10px] text-gray-medium">{rec.created_at ? new Date(rec.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    {rec.agent_name && <Badge className="text-[10px] bg-gray-100 text-gray-600 flex-shrink-0">{rec.agent_name}</Badge>}
                    <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={e => { e.stopPropagation(); deleteRecording(rec.id); }}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Log Call</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Caller Name</Label><Input value={form.caller_name} onChange={e => setForm(f => ({ ...f, caller_name: e.target.value }))} data-testid="call-caller-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.caller_phone} onChange={e => setForm(f => ({ ...f, caller_phone: e.target.value }))} /></div>
              <div><Label>Email</Label><Input value={form.caller_email} onChange={e => setForm(f => ({ ...f, caller_email: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Direction</Label>
                <Select value={form.direction} onValueChange={v => setForm(f => ({ ...f, direction: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="inbound">Inbound</SelectItem><SelectItem value="outbound">Outbound</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Outcome</Label>
                <Select value={form.outcome} onValueChange={v => setForm(f => ({ ...f, outcome: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(OUTCOMES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Duration (seconds)</Label><Input type="number" min={0} value={form.duration_seconds} onChange={e => setForm(f => ({ ...f, duration_seconds: e.target.value }))} /></div>
              <div><Label>Agent Name</Label><Input value={form.agent_name} onChange={e => setForm(f => ({ ...f, agent_name: e.target.value }))} /></div>
            </div>
            <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="follow-up, complaint, upsell" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-call-btn">Log Call</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!showView} onOpenChange={() => setShowView(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{showView?.call_id}</DialogTitle></DialogHeader>
          {showView && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-medium">Caller:</span> <span className="font-medium">{showView.caller_name || '—'}</span></div>
                <div><span className="text-gray-medium">Phone:</span> <span>{showView.caller_phone || '—'}</span></div>
                <div><span className="text-gray-medium">Direction:</span> <Badge className={cn("text-[10px]", (DIRECTIONS[showView.direction] || DIRECTIONS.inbound).color)}>{showView.direction}</Badge></div>
                <div><span className="text-gray-medium">Outcome:</span> <Badge className={cn("text-[10px]", (OUTCOMES[showView.outcome] || OUTCOMES.completed).color)}>{showView.outcome}</Badge></div>
                <div><span className="text-gray-medium">Duration:</span> <span className="font-medium">{formatDuration(showView.duration_seconds)}</span></div>
                <div><span className="text-gray-medium">Agent:</span> <span>{showView.agent_name || '—'}</span></div>
                <div><span className="text-gray-medium">Date:</span> <span>{showView.created_at ? new Date(showView.created_at).toLocaleString() : '—'}</span></div>
              </div>
              {showView.tags?.length > 0 && <div className="flex flex-wrap gap-1">{showView.tags.map((t, i) => <Badge key={i} className="text-[10px] bg-gray-100 text-gray-600">{t}</Badge>)}</div>}
              {showView.notes && <p className="bg-gray-50 p-2 rounded text-gray-medium">{showView.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
